<template>
  <div class="jim-scheduling-container company-maintenance-page">

    <!-- Header row -->
    <div class="page-header-row">
      <div></div>
      <h1 class="page-heading">COMPANY MAINTENANCE</h1>
      <div></div>
    </div>

    <div class="main-content">

      <!-- LEFT SIDE CONTROLS -->
      <div class="left-side-section">

        <button
          class="plan-it-btn"
          :disabled="loading || syncing"
          @click="loadRows"
        >
          {{ loading ? "Loading..." : "Load Rows" }}
        </button>

        <button
          class="plan-it-btn"
          :disabled="syncing || loading || dirtyRows.length > 0"
          @click="syncMH25History"
        >
          {{ syncing ? "Syncing..." : "Sync History" }}
        </button>

        <button class="plan-it-btn" :disabled="!rowsLoaded" @click="exportCSV">
          Export CSV
        </button>

        <button class="plan-it-btn" :disabled="!rowsLoaded" @click="printPage">
          Print
        </button>

        <button
          class="plan-it-btn"
          :disabled="!dirtyRows.length || syncing || loading"
          @click="saveChanges"
        >
          Save Changes
        </button>

        <div class="control">
          <label>Search Item / Description</label>
          <input
            v-model="searchText"
            type="text"
            placeholder="Search..."
          />
        </div>

        <div class="control">
          <label>Machine</label>
          <select v-model="selectedMachine">
            <option value="">All Machines</option>
            <option
              v-for="m in machineOptions"
              :key="m"
              :value="m"
            >
              {{ m }}
            </option>
          </select>
        </div>

        <button class="plan-it-btn" :disabled="!rowsLoaded" @click="clearFilters">
          Clear Filters
        </button>

        <div v-if="error" class="panel error-box">
          {{ error }}
        </div>

        <div v-if="successMessage" class="panel success-box">
          {{ successMessage }}
        </div>

      </div>

      <!-- RIGHT SIDE CONTENT -->
      <div class="right-side-section">

        <!-- Summary -->
        <div v-if="rowsLoaded" class="summary-bar">
          <div class="summary-item">
            <div class="summary-label">Location</div>
            <div class="summary-value">{{ selectedLocation }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Rows</div>
            <div class="summary-value">{{ filteredRows.length }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Machines</div>
            <div class="summary-value">{{ machineOptions.length }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Avg Throughput</div>
            <div class="summary-value">{{ formatNumber(summary.avgThroughput) }}</div>
          </div>

          <div class="summary-item">
            <div class="summary-label">Unsaved</div>
            <div class="summary-value">{{ dirtyRows.length }}</div>
          </div>
        </div>

        <!-- Scrollable Table -->
        <div v-if="rowsLoaded" class="sheet-scroll-container">
          <div class="sheet-wrapper">
            <div class="sheet-page panel">

              <div class="sheet-page-header">
                <h2>Planning Parameters — {{ selectedLocation }}</h2>
                <div class="small-muted">
                  Throughput • Recommended WO Qty • Safety Stock
                </div>
              </div>

              <div class="table-scroll">
                <table class="maintenance-table">
                  <thead>
                    <tr>
                      <th class="sticky-col sortable" @click="sortBy('machine')">
                        Machine {{ sortIcon("machine") }}
                      </th>

                      <th class="sortable" @click="sortBy('item_no')">
                        Item No {{ sortIcon("item_no") }}
                      </th>

                      <th class="desc-col sortable" @click="sortBy('description')">
                        Description {{ sortIcon("description") }}
                      </th>

                      <th class="sortable" @click="sortBy('throughput')">
                        Throughput {{ sortIcon("throughput") }}
                      </th>

                      <th class="sortable" @click="sortBy('throughput_per_hour')">
                        Throughput / Hr {{ sortIcon("throughput_per_hour") }}
                      </th>

                      <th class="sortable" @click="sortBy('recommended_wo_qty')">
                        Recommended WO Qty {{ sortIcon("recommended_wo_qty") }}
                      </th>

                      <th class="sortable" @click="sortBy('max_inventory')">
                        Max Inventory {{ sortIcon("max_inventory") }}
                      </th>

                      <th class="sortable" @click="sortBy('qty_on_hand')">
                        Qty On Hand {{ sortIcon("qty_on_hand") }}
                      </th>

                      <th class="sortable" @click="sortBy('safety_stk')">
                        Safety Stock {{ sortIcon("safety_stk") }}
                      </th>

                      <th class="notes-col sortable" @click="sortBy('notes')">
                        Standing Note {{ sortIcon("notes") }}
                      </th>
                    </tr>

                    <tr class="filter-row">
                      <th class="sticky-col">
                        <input v-model="columnFilters.machine" class="filter-input" placeholder="Filter" />
                      </th>

                      <th>
                        <input v-model="columnFilters.item_no" class="filter-input" placeholder="Filter" />
                      </th>

                      <th class="desc-col">
                        <input v-model="columnFilters.description" class="filter-input" placeholder="Filter" />
                      </th>

                      <th>
                        <input v-model="columnFilters.throughput" class="filter-input" placeholder="Filter" />
                      </th>

                      <th>
                        <input v-model="columnFilters.throughput_per_hour" class="filter-input" placeholder="Filter" />
                      </th>

                      <th>
                        <input v-model="columnFilters.recommended_wo_qty" class="filter-input" placeholder="Filter" />
                      </th>

                      <th>
                        <input v-model="columnFilters.max_inventory" class="filter-input" placeholder="Filter" />
                      </th>

                      <th>
                        <input v-model="columnFilters.qty_on_hand" class="filter-input" placeholder="Filter" />
                      </th>

                      <th>
                        <input
                          v-model="columnFilters.safety_stk"
                          class="filter-input"
                          placeholder="Filter"
                        />
                      </th>

                      <th class="notes-col">
                        <input
                          v-model="columnFilters.notes"
                          class="filter-input"
                          placeholder="Filter"
                        />
                      </th>
                      </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="r in filteredRows"
                      :key="rowKey(r)"
                      :class="rowClass(r)"
                    >
                      <td class="sticky-col">{{ r.machine }}</td>
                      <td>{{ r.item_no }}</td>
                      <td class="desc-col">{{ r.description }}</td>

                      <td>
                        <span
                          v-if="!isEditingCell(r, 'throughput')"
                          class="editable-cell"
                          @dblclick="startEdit(r, 'throughput')"
                        >
                          {{ formatNumber(r.throughput) }}
                        </span>

                        <input
                          v-else
                          v-model.number="r.throughput"
                          type="number"
                          step="0.01"
                          class="table-input"
                          @input="markDirty(r)"
                          @change="markDirty(r)"
                          @blur="stopEdit"
                          @keyup.enter="stopEdit"
                        />
                      </td>

                      <td>
                        <span
                          v-if="!isEditingCell(r, 'throughput_per_hour')"
                          class="editable-cell"
                          @dblclick="startEdit(r, 'throughput_per_hour')"
                        >
                          {{ formatNumber(r.throughput_per_hour) }}
                        </span>

                        <input
                          v-else
                          v-model.number="r.throughput_per_hour"
                          type="number"
                          step="0.01"
                          class="table-input"
                          @input="markDirty(r)"
                          @change="markDirty(r)"
                          @blur="stopEdit"
                          @keyup.enter="stopEdit"
                        />
                      </td>

                      <td>
                        <span
                          v-if="!isEditingCell(r, 'recommended_wo_qty')"
                          class="editable-cell"
                          @dblclick="startEdit(r, 'recommended_wo_qty')"
                        >
                          {{ formatNumber(r.recommended_wo_qty) }}
                        </span>

                        <input
                          v-else
                          v-model.number="r.recommended_wo_qty"
                          type="number"
                          step="1"
                          class="table-input"
                          @input="markDirty(r)"
                          @change="markDirty(r)"
                          @blur="stopEdit"
                          @keyup.enter="stopEdit"
                        />
                      </td>

                      <td>
                        <span
                          v-if="!isEditingCell(r, 'max_inventory')"
                          class="editable-cell"
                          @dblclick="startEdit(r, 'max_inventory')"
                        >
                          {{ formatNumber(r.max_inventory) }}
                        </span>

                        <input
                          v-else
                          v-model.number="r.max_inventory"
                          type="number"
                          step="1"
                          class="table-input"
                          @input="markDirty(r)"
                          @change="markDirty(r)"
                          @blur="stopEdit"
                          @keyup.enter="stopEdit"
                        />
                      </td>
                      
                      <td>{{ formatNumber(r.qty_on_hand) }}</td>

                      <td>{{ formatNumber(r.safety_stk) }}</td>

                      <td class="notes-col">
                      <input
                        v-model="r.notes"
                        type="text"
                        class="notes-input"
                        placeholder="Standing production instruction..."
                        @input="onStandingNoteInput(r)"
                      />
                    </td>
                    </tr> 

                    <tr v-if="rowsLoaded && filteredRows.length === 0">
                      <td colspan="8" class="empty-row">
                        No rows match the current filters.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="print-footer">
                <div class="footer-text">
                  Company Maintenance controls Pablo item planning values by location, machine, and item.
                  Safety Stock is sourced from Macola inventory location data.
                </div>
              </div>

            </div>
          </div>
        </div>

        <div v-else class="panel notes-panel">
          <div class="notes-title">Instructions</div>
          <div class="notes-body">
            Click Load Rows to pull throughput, recommended work order quantity, lot size, and safety stock details
            for the selected Pablo location.
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "CompanyMaintenance",

  data() {
    return {
      rows: [],
      originalRowsByKey: {},
      dirtyKeys: {},

      rowsLoaded: false,
      loading: false,
      syncing: false,

      searchText: "",
      selectedMachine: "",

      sortKey: "",
      sortDirection: "asc",

      columnFilters: {
        machine: "",
        item_no: "",
        description: "",
        throughput: "",
        throughput_per_hour: "",
        recommended_wo_qty: "",
        max_inventory: "",
        qty_on_hand: "",
        safety_stk: "",
        notes: ""
      },

      filterTimer: null,
      activeSearchText: "",
      activeColumnFilters: {
        machine: "",
        item_no: "",
        description: "",
        throughput: "",
        throughput_per_hour: "",
        recommended_wo_qty: "",
        max_inventory: "",
        qty_on_hand: "",
        safety_stk: "",
        notes: ""
      },

      editingCell: {
        key: "",
        field: ""
      },

      error: null,
      successMessage: null,

      summary: {
        avgThroughput: 0
      }
    };
  },

  computed: {
    selectedLocation() {
      return this.$root.selectedLocation || "D";
    },

    machineOptions() {
      return [...new Set(
        (this.rows || [])
          .map(r => r.machine)
          .filter(Boolean)
      )].sort();
    },

    filteredRows() {
      let result = [...(this.rows || [])];

      if (this.selectedMachine) {
        result = result.filter(r => r.machine === this.selectedMachine);
      }

      const q = String(this.activeSearchText || "").trim().toLowerCase();

      if (q) {
        result = result.filter(r => {
          return Object.keys(this.activeColumnFilters).some(key => {
            return String(r[key] ?? "").toLowerCase().includes(q);
          });
        });
      }

      Object.entries(this.activeColumnFilters).forEach(([key, filterValue]) => {
        const filter = String(filterValue || "").trim().toLowerCase();

        if (!filter) return;

        result = result.filter(r => {
          const value = String(r[key] ?? "").toLowerCase();
          return value.includes(filter);
        });
      });

      if (this.sortKey) {
        const key = this.sortKey;
        const direction = this.sortDirection === "desc" ? -1 : 1;

        result.sort((a, b) => {
          const av = a[key];
          const bv = b[key];

          const an = Number(av);
          const bn = Number(bv);

          const bothNumeric =
            av !== null &&
            av !== "" &&
            bv !== null &&
            bv !== "" &&
            !Number.isNaN(an) &&
            !Number.isNaN(bn);

          if (bothNumeric) {
            return (an - bn) * direction;
          }

          return String(av ?? "")
            .localeCompare(String(bv ?? ""), undefined, {
              numeric: true,
              sensitivity: "base"
            }) * direction;
        });
      }

      return result;
    },

    dirtyRows() {
      return (this.rows || []).filter(r => this.dirtyKeys[this.rowKey(r)]);
    }
  },

  watch: {
    selectedLocation(newLoc, oldLoc) {
      if (newLoc !== oldLoc) {
        this.rows = [];
        this.originalRowsByKey = {};
        this.dirtyKeys = {};
        this.rowsLoaded = false;
        this.searchText = "";
        this.selectedMachine = "";
        this.error = null;
        this.successMessage = null;
      }
    },

    searchText() {
      this.queueFilterUpdate();
    },

    columnFilters: {
      deep: true,
      handler() {
        this.queueFilterUpdate();
      }
    }
    
  },

  async mounted() {
    await this.loadRows();
  },

  methods: {
    async loadRows() {
      this.error = null;
      this.successMessage = null;
      this.loading = true;
      this.rowsLoaded = false;
      this.rows = [];
      this.originalRowsByKey = {};
      this.dirtyKeys = {};

      try {
        const { data } = await axios.get("/getCompanyMaintenanceRows", {
          params: {
            location: this.selectedLocation
          }
        });

        this.rows = (data.rows || []).map(r => ({
          loc: r.loc || this.selectedLocation,
          machine: r.machine || "",
          item_no: r.item_no || "",
          description: r.description || "",

          throughput: this.nullableNumber(r.throughput),
          throughput_per_hour: this.nullableNumber(r.throughput_per_hour),

          recommended_wo_qty: this.nullableNumber(r.recommended_wo_qty),
          max_inventory: this.nullableNumber(r.max_inventory),          

          qty_on_hand: this.nullableNumber(r.qty_on_hand),
          safety_stk: this.nullableNumber(r.safety_stk),

          notes: r.notes || "",
          unit: r.unit || "units/day"
        }));

        this.rows.forEach(r => {
          this.originalRowsByKey[this.rowKey(r)] = JSON.parse(JSON.stringify(r));
        });

        this.rowsLoaded = true;
        this.computeSummary();
      } catch (err) {
        console.error("loadRows error:", err);
        this.error =
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to load company maintenance rows.";
      } finally {
        this.loading = false;
      }
    },

    async syncMH25History() {
      if (this.dirtyRows.length > 0) {
        this.error =
          "Save or discard your unsaved changes before syncing MH25 history.";
        return;
      }

      this.error = null;
      this.successMessage = null;
      this.syncing = true;

      try {
        const { data } = await axios.post(
          "/syncCompanyMaintenanceFromMacola",
          {
            location: this.selectedLocation
          }
        );

        const historyFound = Number(
          data.history_combinations_found || 0
        );

        const rowsInserted = Number(
          data.rows_inserted || 0
        );

        const rowsAlreadyPresent =
          data.rows_already_present !== undefined
            ? Number(data.rows_already_present || 0)
            : Math.max(historyFound - rowsInserted, 0);

        /*
          Reload the maintenance table so newly inserted MH25 rows
          appear immediately.
        */
        await this.loadRows();

        /*
          loadRows clears successMessage, so set the sync result
          only after the reload finishes.
        */
        if (!this.error) {
          if (historyFound === 0) {
            this.successMessage =
              `No MH25 work-order history was found for location ${this.selectedLocation}.`;
          } else if (rowsInserted === 0) {
            this.successMessage =
              `MH25 sync complete for ${this.selectedLocation}. ` +
              `${historyFound} historical combination(s) found; ` +
              `all ${rowsAlreadyPresent} were already present.`;
          } else {
            this.successMessage =
              `MH25 sync complete for ${this.selectedLocation}. ` +
              `${rowsInserted} new row(s) added; ` +
              `${rowsAlreadyPresent} already present.`;
          }
        }
      } catch (err) {
        console.error("syncMH25History error:", err);

        this.error =
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to sync MH25 work-order history from Macola.";
      } finally {
        this.syncing = false;
      }
    },

    async saveChanges() {
      this.error = null;
      this.successMessage = null;

      const changes = this.dirtyRows.map(r => ({
        loc: r.loc,
        machine: r.machine,
        item_no: r.item_no,

        throughput: this.cleanNumber(r.throughput),
        throughput_per_hour: this.cleanNumber(r.throughput_per_hour),
        recommended_wo_qty: this.cleanNumber(r.recommended_wo_qty),
        max_inventory: this.cleanNumber(r.max_inventory),

        notes: String(r.notes ?? "").trim()
      }));

      if (!changes.length) return;

      try {
        await axios.put("/updateCompanyMaintenanceRows", {
          location: this.selectedLocation,
          rows: changes
        });

        this.successMessage = `Saved ${changes.length} row(s).`;

        this.rows.forEach(r => {
          this.originalRowsByKey[this.rowKey(r)] = JSON.parse(JSON.stringify(r));
        });

        this.dirtyKeys = {};
      } catch (err) {
        console.error("saveChanges error:", err);
        this.error =
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          "Failed to save changes. Confirm that /updateCompanyMaintenanceRows exists in the backend.";
      }
    },

    onStandingNoteInput(row) {
      const loc = String(
        row.loc || this.selectedLocation || ""
      )
        .trim()
        .toUpperCase();

      const itemNo = String(
        row.item_no || ""
      ).trim();

      const note = String(
        row.notes ?? ""
      );

      /*
        Standing notes belong to loc + item_no,
        not loc + machine + item_no.

        If the same item appears on multiple machines,
        update every visible copy so the screen remains
        consistent.
      */
      (this.rows || []).forEach(r => {
        const rLoc = String(
          r.loc || this.selectedLocation || ""
        )
          .trim()
          .toUpperCase();

        const rItemNo = String(
          r.item_no || ""
        ).trim();

        if (
          rLoc === loc &&
          rItemNo === itemNo
        ) {
          r.notes = note;
        }
      });

      this.markDirty(row);
    },
    
    markDirty(row) {
      this.successMessage = null;
      const key = this.rowKey(row);

      this.dirtyKeys = {
        ...this.dirtyKeys,
        [key]: true
      };
    },

    rowKey(row) {
      return [
        String(row.loc || "").trim().toUpperCase(),
        String(row.machine || "").trim(),
        String(row.item_no || "").trim()
      ].join("|");
    },

    rowClass(row) {
      const key = this.rowKey(row);

      if (this.dirtyKeys[key]) return "dirtyRow";

      const safety = Number(row.safety_stk || 0);
      const onHand = Number(row.qty_on_hand || 0);

      if (safety > 0 && onHand < safety) return "belowSafety";

      return "";
    },

    sortBy(key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDirection = "asc";
      }
    },

    sortIcon(key) {
      if (this.sortKey !== key) return "↕";
      return this.sortDirection === "asc" ? "▲" : "▼";
    },

    clearFilters() {
      this.searchText = "";
      this.selectedMachine = "";

      this.columnFilters = {
        machine: "",
        item_no: "",
        description: "",
        throughput: "",
        throughput_per_hour: "",
        recommended_wo_qty: "",
        max_inventory: "",
        qty_on_hand: "",
        safety_stk: "",
        notes: ""
      };

      this.activeSearchText = "";

      this.activeColumnFilters = {
        machine: "",
        item_no: "",
        description: "",
        throughput: "",
        throughput_per_hour: "",
        recommended_wo_qty: "",
        max_inventory: "",
        qty_on_hand: "",
        safety_stk: "",
        notes: ""
      };

      this.sortKey = "";
      this.sortDirection = "asc";
    },

    queueFilterUpdate() {
      clearTimeout(this.filterTimer);

      this.filterTimer = setTimeout(() => {
        this.activeSearchText = this.searchText;

        this.activeColumnFilters = {
          ...this.columnFilters
        };
      }, 200);
    },

    startEdit(row, field) {
      this.editingCell = {
        key: this.rowKey(row),
        field
      };
    },

    stopEdit() {
      this.editingCell = {
        key: "",
        field: ""
      };
    },

    isEditingCell(row, field) {
      return (
        this.editingCell.key === this.rowKey(row) &&
        this.editingCell.field === field
      );
    },

    computeSummary() {
      const valid = (this.rows || [])
        .map(r => Number(r.throughput || 0))
        .filter(v => !Number.isNaN(v) && v > 0);

      if (!valid.length) {
        this.summary.avgThroughput = 0;
        return;
      }

      this.summary.avgThroughput =
        valid.reduce((a, v) => a + v, 0) / valid.length;
    },

    exportCSV() {
      const headers = [
        "Location",
        "Machine",
        "Item No",
        "Description",
        "Throughput",
        "Throughput Per Hour",
        "Recommended WO Qty",
        "Qty On Hand",
        "Safety Stock"
      ];

      const rows = this.filteredRows.map(r => [
        r.loc,
        r.machine,
        r.item_no,
        this.csvSafe(r.description),
        r.throughput ?? "",
        r.throughput_per_hour ?? "",
        r.recommended_wo_qty ?? "",
        r.qty_on_hand ?? "",
        r.safety_stk ?? ""
      ]);

      const csv = [headers, ...rows]
        .map(row => row.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `CompanyMaintenance_${this.selectedLocation}.csv`;
      link.click();

      URL.revokeObjectURL(url);
    },

    printPage() {
      window.print();
    },

    nullableNumber(v) {
      if (v === null || v === undefined || v === "") return null;
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    },

    cleanNumber(v) {
      if (v === null || v === undefined || v === "") return null;
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    },

    csvSafe(v) {
      return String(v || "").replace(/\r?\n/g, " ").trim();
    },

    formatNumber(v) {
      if (v === null || v === undefined || v === "") return "";
      const n = Number(v);
      if (Number.isNaN(n)) return "";

      return new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 2
      }).format(n);
    }
  }
};
</script>

<style scoped>

/* PAGE BASE */
.company-maintenance-page {
  background-color: #0b0101;
  min-height: 100vh;
  padding: 14px;
}

/* BASE CONTAINER — matches JIMScheduling */
.jim-scheduling-container {
  font-family: Garamond, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #cbbfbf;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #0b0101;
  min-height: 100vh;
}

/* Header row */
.page-header-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 10px;
}

.page-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 36px;
  color: #cbbfbf;
  margin: 0;
  text-align: center;
}

/* MAIN LAYOUT */
.main-content {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  width: 100%;
  padding: 0 14px;
  box-sizing: border-box;
  align-items: start;
}

/* Left-side controls */
.left-side-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 120px;
}

/* Right-side workspace */
.right-side-section {
  width: 100%;
  min-width: 0;
}

/* Outer sheet area */
.sheet-scroll-container {
  width: 100%;
  border-radius: 10px;
  background-color: #0b0101;
  padding-right: 10px;
  box-sizing: border-box;
  overflow: visible;
}

/* Pablo dark panel */
.panel {
  background-color: #0b0101;
  border: 1px solid #888;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
}

/* Controls */
.control {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  width: 100%;
}

.control label {
  font-size: 13px;
  color: #cbbfbf;
  font-weight: 600;
}

.control input,
.control select,
select {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

/* Pablo buttons */
.plan-it-btn {
  border-radius: 10px;
  padding: 8px 16px;
  width: 160px;
  background-color: #f3f4f6;
  border: 1px solid #cfd3d8;
  color: #333;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
  height: 38px;
}

.plan-it-btn:hover:not(:disabled) {
  background-color: #e5e7eb;
  border-color: #bfc5cc;
}

.plan-it-btn:disabled {
  background-color: #e5e7eb;
  color: #888;
  cursor: not-allowed;
}

/* Error / success */
.error-box {
  border-color: #aa4444;
  background: #2a0b0b;
  color: #fca5a5;
  width: 100%;
}

.success-box {
  border-color: #4d7c0f;
  background: #10220b;
  color: #bbf7d0;
  width: 100%;
}

/* Summary bar */
.summary-bar {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #888;
  margin: 0 0 12px;
  background-color: #708090a4;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;
}

.summary-item {
  min-width: 140px;
  flex: 1 1 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-label {
  font-size: 18px;
  opacity: 0.85;
  line-height: 1.1;
}

.summary-value {
  font-size: 24px;
  font-weight: bold;
  color: #fff;
  line-height: 1.1;
  margin-top: 2px;
}

/* Notes panel */
.notes-panel {
  margin-bottom: 12px;
}

.notes-title {
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 6px;
}

.notes-body {
  font-size: 14px;
  line-height: 1.35;
  white-space: pre-wrap;
}

/* Page wrapper */
.sheet-wrapper {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Sheet panel */
.sheet-page {
  background-color: #708090a4;
  border-radius: 10px;
  border: 1px solid #888;
}

/* Page header */
.sheet-page-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}

.sheet-page-header h2 {
  margin: 0;
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: 22px;
  color: #fff;
}

.small-muted {
  font-size: 12px;
  opacity: 0.8;
  color: #fff;
}

/*
  TABLE SCROLLING

  This is now the single scroll container for BOTH:
  - vertical scrolling
  - horizontal scrolling

  Keeping both directions on the same element allows
  position: sticky to work correctly on the table headers.
*/
.table-scroll {
  max-height: 70vh;
  overflow: auto;
  padding-bottom: 6px;
  position: relative;
}

/* Scrollbar */
.table-scroll::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.table-scroll::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 8px;
}

.table-scroll::-webkit-scrollbar-track {
  background: #111;
}

/* Maintenance table */
.maintenance-table {
  width: 100%;
  min-width: 1300px;

  /*
    Separate borders behave more reliably with sticky table headers
    than border-collapse: collapse.
  */
  border-collapse: separate;
  border-spacing: 0;
}

.maintenance-table th,
.maintenance-table td {
  border-right: 1px solid #333;
  border-bottom: 1px solid #333;
  padding: 6px 8px;
  font-size: 13px;
  text-align: center;
}

.maintenance-table th:first-child,
.maintenance-table td:first-child {
  border-left: 1px solid #333;
}

.maintenance-table thead tr:first-child th {
  border-top: 1px solid #333;
}

/*
  =========================================================
  STICKY COLUMN HEADINGS
  =========================================================

  The field names remain visible while scrolling vertically.
*/
.maintenance-table thead tr:first-child th {
  position: sticky;
  top: 0;
  z-index: 6;
  background-color: #0b0101;
  color: #cbbfbf;
  font-weight: bold;
}

/*
  =========================================================
  STICKY FILTER ROW
  =========================================================

  The filter inputs remain directly below the column headings.
*/
.maintenance-table thead .filter-row th {
  position: sticky;
  top: 31px;
  z-index: 5;
  background-color: #160808 !important;
}

/*
  =========================================================
  STICKY LEFT MACHINE COLUMN
  =========================================================

  Machine remains visible while scrolling horizontally.
*/
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 4;
  background: #0b0101;
  min-width: 110px;
  text-align: left !important;
  color: #cbbfbf;
}

/*
  Header Machine cell needs to sit above both
  the sticky header and sticky left column.
*/
.maintenance-table thead tr:first-child th.sticky-col {
  z-index: 8;
  background-color: #0b0101;
}

/*
  Filter-row Machine cell needs to sit above the body
  sticky column while remaining below the main header.
*/
.maintenance-table thead .filter-row th.sticky-col {
  z-index: 7;
  background-color: #160808 !important;
}

/* Column sizing */
.desc-col {
  min-width: 240px;
  text-align: left !important;
}

.notes-col {
  min-width: 220px;
  text-align: left !important;
}

/* Editable numeric cells */
.table-input {
  width: 90px;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  text-align: center;
}

/* Notes input */
.notes-input {
  width: 95%;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
}

/* Hover */
.maintenance-table tbody tr:hover td {
  background-color: #5595b7c2;
  color: #fff;
}

/*
  Keep sticky Machine column visually consistent
  while hovering across a row.
*/
.maintenance-table tbody tr:hover td.sticky-col {
  background-color: #5595b7c2;
  color: #fff;
}

.empty-row {
  padding: 14px;
  font-size: 14px;
  opacity: 0.75;
  text-align: center;
}

/* Row indicators */
.dirtyRow td {
  background-color: #57420d !important;
  color: #fff3c4 !important;
}

.belowSafety td {
  background-color: #7a2323 !important;
  color: #fff !important;
}

/* Footer */
.print-footer {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #333;
  color: #fff;
  font-size: 12px;
  line-height: 1.35;
}

.footer-text {
  white-space: pre-wrap;
}

/* Sortable headers */
.sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.sortable:hover {
  background-color: #1f2937 !important;
}

/* Filter inputs */
.filter-input {
  width: 95%;
  padding: 5px 6px;
  border-radius: 7px;
  border: 1px solid #444;
  background-color: #111;
  color: #cbbfbf;
  font-family: inherit;
  font-size: 12px;
  box-sizing: border-box;
}

.filter-input::placeholder {
  color: #888;
}

/* Editable cell display */
.editable-cell {
  display: block;
  min-height: 24px;
  line-height: 24px;
  cursor: pointer;
  border-radius: 6px;
}

.editable-cell:hover {
  background-color: #111;
  color: #fff;
}

/* =========================================================
   PRINT STYLING
   ========================================================= */
@media print {

  .left-side-section,
  .error-box,
  .success-box,
  .summary-bar {
    display: none !important;
  }

  .main-content {
    display: block !important;
    padding: 0 !important;
  }

  .sheet-scroll-container {
    max-height: none !important;
    overflow: visible !important;
    padding-right: 0 !important;
  }

  .sheet-page {
    page-break-after: always;
    break-after: page;
    display: flex !important;
    flex-direction: column !important;
    min-height: 95vh !important;
    background: white !important;
    border: none !important;
  }

  .table-scroll {
    max-height: none !important;
    flex: 1 1 auto !important;
    overflow: visible !important;
  }

  .jim-scheduling-container,
  .panel {
    background: white !important;
    color: black !important;
    border: none !important;
  }

  .page-heading,
  .sheet-page-header h2,
  .small-muted {
    color: black !important;
  }

  .maintenance-table {
    min-width: 0 !important;
    width: 100% !important;
  }

  .maintenance-table th,
  .maintenance-table td {
    font-size: 10px !important;
    padding: 4px !important;
    color: black !important;
  }

  /*
    Disable sticky positioning for print.
  */
  .maintenance-table thead tr:first-child th,
  .maintenance-table thead .filter-row th,
  .sticky-col {
    position: static !important;
    top: auto !important;
    left: auto !important;
  }

  .maintenance-table th {
    background: #eee !important;
    color: black !important;
  }

  .maintenance-table thead .filter-row th {
    background: #eee !important;
    color: black !important;
  }

  .sticky-col {
    background: white !important;
    color: black !important;
  }

  .table-input,
  .notes-input {
    border: none !important;
    background: transparent !important;
    color: black !important;
    padding: 0 !important;
    width: 100% !important;
  }

  .dirtyRow td,
  .belowSafety td {
    background: white !important;
    color: black !important;
  }

  .print-footer {
    margin-top: auto !important;
    border-top: 1px solid #999 !important;
    color: black !important;
  }

  .print-footer * {
    color: black !important;
  }
}

</style>